import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, CheckCircle2, AlertCircle, Sparkles, Bug, Lightbulb, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useStore } from "../../stores/store";

interface Props {
  open: boolean;
  onClose: () => void;
}

const PROMPTS = [
  { icon: Bug, label: "Achei um bug", color: "text-rose-300" },
  { icon: Lightbulb, label: "Tive uma ideia", color: "text-amber-300" },
  { icon: MessageCircle, label: "Quero mandar um feedback", color: "text-sky-300" },
] as const;

const MIN_LEN = 10;
const MAX_LEN = 5000;

export function BetaFeedbackModal({ open, onClose }: Props) {
  const userEmail = useStore((s) => s.userEmail);

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    setMessage("");
    setSent(false);
    setError(null);
    setSending(false);
    const t = setTimeout(() => textareaRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function handleSubmit() {
    const trimmed = message.trim();
    if (trimmed.length < MIN_LEN) {
      setError(`Escreva pelo menos ${MIN_LEN} caracteres pra eu entender direito.`);
      return;
    }
    setError(null);
    setSending(true);
    try {
      await api.sendFeedback(trimmed, userEmail || undefined);
      setSent(true);
      setTimeout(() => onClose(), 1800);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao enviar.";
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  function insertPrompt(label: string) {
    const prefix = `${label}: `;
    setMessage((cur) => (cur.startsWith(prefix) ? cur : prefix + cur.replace(/^(Achei um bug|Tive uma ideia|Quero mandar um feedback): /, "")));
    textareaRef.current?.focus();
  }

  const remaining = MAX_LEN - message.length;
  const tooShort = message.trim().length > 0 && message.trim().length < MIN_LEN;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="beta-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            key="beta-card"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[520px] overflow-hidden rounded-2xl border border-white/10 bg-bg-2/[0.98] p-6 shadow-elevated backdrop-blur-md"
          >
            {/* Header gradient line */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/60 to-transparent" />

            <div className="mb-4 flex items-start gap-3">
              <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 shadow-[0_0_20px_rgba(168,85,247,0.45)]">
                <div className="absolute inset-[1.5px] rounded-[10px] bg-bg-0/70 backdrop-blur-md" />
                <Sparkles className="relative h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold tracking-tight text-slate-100">
                  Espaço BETA
                </h2>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-400">
                  Achou um bug, teve uma ideia ou só quer mandar um recado?
                  Tô lendo cada um, prometo.
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-md p-1 text-slate-400 transition hover:bg-white/5 hover:text-slate-200"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Atalhos de prefixo */}
            <div className="mb-3 flex flex-wrap gap-1.5">
              {PROMPTS.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => insertPrompt(p.label)}
                    disabled={sending || sent}
                    className="group flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11.5px] text-slate-300 transition hover:border-white/20 hover:bg-white/[0.06] disabled:opacity-50"
                  >
                    <Icon className={`h-3 w-3 ${p.color}`} />
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Textarea */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value.slice(0, MAX_LEN));
                  if (error) setError(null);
                }}
                disabled={sending || sent}
                placeholder="Conta aí. Pode escrever solto, sem formalidade. Quanto mais detalhe melhor, mas qualquer coisa ajuda."
                rows={6}
                className="w-full resize-none rounded-xl border border-white/10 bg-black/30 p-3 text-sm leading-relaxed text-slate-100 placeholder:text-slate-500 focus:border-violet-400/60 focus:outline-none focus:ring-2 focus:ring-violet-500/20 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <div className="mt-1 flex items-center justify-between text-[11px]">
                <span className={tooShort ? "text-amber-300" : "text-slate-500"}>
                  {tooShort ? `mínimo ${MIN_LEN} caracteres` : ` `}
                </span>
                <span className={remaining < 200 ? "text-amber-300" : "text-slate-500"}>
                  {remaining} restantes
                </span>
              </div>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 flex items-start gap-2 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
              {sent && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200"
                >
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>Recebi, valeu! Vou ler com calma.</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer: identificação + ação */}
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-[11px] text-slate-500">
                {userEmail
                  ? `assinado como ${userEmail}`
                  : "envio anônimo"}
              </span>
              <button
                onClick={handleSubmit}
                disabled={sending || sent || message.trim().length < MIN_LEN}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 via-fuchsia-500 to-violet-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_22px_-8px_rgba(168,85,247,0.6)] transition hover:shadow-[0_8px_28px_-6px_rgba(168,85,247,0.75)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : sent ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span>{sending ? "Enviando…" : sent ? "Enviado" : "Enviar"}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
